import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractDialogResultData, AbstractFormDialog } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';
import { CropAid, FertilizerTypeEnum, LegalNitrogenWorkingCoefficient, SalesOrderPeriod, SiteState, SoilType } from 'src/app/shared/models';
import { DisplayService } from 'src/app/shared/services/display.service';
import { getCropAids, getFertilizationSalesOrderPeriods, getSoilTypes } from 'src/app/store/selectors';

import { LegalNitrogenWorkingCoefficientDialogModel } from './legal-nitrogen-working-coefficient-dialog.model';

@Component({
  selector: 'app-legal-nitrogen-working-coefficient-dialog',
  templateUrl: './legal-nitrogen-working-coefficient-dialog.component.html',
  standalone: false,
})
export class LegalNitrogenWorkingCoefficientDialogComponent extends AbstractFormDialog<LegalNitrogenWorkingCoefficient> implements OnInit, OnDestroy {
  data = inject<LegalNitrogenWorkingCoefficientDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<MatDialogRef<LegalNitrogenWorkingCoefficientDialogComponent, AbstractDialogResultData<LegalNitrogenWorkingCoefficient>>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  displayService = inject(DisplayService);
  private readonly locale = inject(LocaleService);
  private readonly store = inject<Store<SiteState>>(Store);

  formGroup = this.fb.group({
    calendarYear: [this.data.legalNitrogenWorkingCoefficient.calendarYear, [Validators.required]],
    salesOrderPeriodId: [this.data.legalNitrogenWorkingCoefficient.salesOrderPeriodId, [Validators.required]],
    fertilizerType: [this.data.legalNitrogenWorkingCoefficient.fertilizerType, [Validators.required]],
    cropAidId: [this.data.legalNitrogenWorkingCoefficient.cropAidId, [Validators.required]],
    soilTypeId: [this.data.legalNitrogenWorkingCoefficient.soilTypeId, [Validators.required]],
    nitrogenWorkingCoefficient: [this.data.legalNitrogenWorkingCoefficient.nitrogenWorkingCoefficient, [Validators.required]],
  });
  soilTypes$ = this.store.pipe(select(getSoilTypes));
  cropAids$ = this.store.pipe(select(getCropAids));
  defaultYear = new Date().getFullYear();
  years = [-1, 0, 1].map((n) => this.defaultYear + n);
  salesOrderPeriods$ = this.store.pipe(select(getFertilizationSalesOrderPeriods));
  soilTypes: SoilType[] = [];
  cropAids: CropAid[] = [];
  salesOrderPeriods: SalesOrderPeriod[] = [];
  fertilizerTypes = Object.values(FertilizerTypeEnum);
  filteredFertilizerTypes$ = of(this.fertilizerTypes);
  filteredSalesOrderPeriods$: Observable<SalesOrderPeriod[]>;
  filteredCropAids$: Observable<CropAid[]>;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  ngOnInit() {
    this.formGroup.get('calendarYear').setValue(this.defaultYear);

    this.soilTypes$.pipe(takeUntil(this.destroyed$)).subscribe((soilTypes) => (this.soilTypes = soilTypes));
    this.cropAids$.pipe(takeUntil(this.destroyed$)).subscribe((cropAids) => (this.cropAids = cropAids));
    this.salesOrderPeriods$.pipe(takeUntil(this.destroyed$)).subscribe((salesOrderPeriods) => (this.salesOrderPeriods = salesOrderPeriods));

    combineLatest([this.salesOrderPeriods$, this.formGroup.get('calendarYear').valueChanges.pipe(startWith(this.defaultYear))])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([salesOrderPeriods, year]) => {
        this.filteredSalesOrderPeriods$ = of(salesOrderPeriods.filter((period) => period.year === year));
      });

    this.filteredCropAids$ = this.formGroup.controls.cropAidId.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value: string) => this.filterCropAids(value)),
    );
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  confirm() {
    const result: LegalNitrogenWorkingCoefficient = {
      id: 0,
      ...this.data.legalNitrogenWorkingCoefficient,
      ...this.formGroup.getRawValue(),
    };
    super.confirm(result);
  }

  displayFertilizerType() {
    return (value?: string) => this.locale.translate(value);
  }

  displayCropAid() {
    return (cropAidId?: number) => (cropAidId ? this.findCropAids(cropAidId)?.name : '');
  }

  findCropAids(cropAidId: number) {
    return this.cropAids.find((cropAid) => cropAid.id === cropAidId);
  }

  filterCropAids(value: string) {
    const filterValue = value.toLowerCase();
    return !this.cropAids ? [] : this.cropAids.filter((option) => option.name.toLowerCase().includes(filterValue));
  }
}
